<template>
    <AdminLayout>
        <div class="p-6">
            <div class="relative">
                <div class="absolute top-0 right-0">
                    <i class="pi pi-file-pdf text-[#C5C6CC] cursor-pointer text-xl" @click="exportPdf"></i>
                </div>
                <h1 class="text-2xl font-bold mb-4">Subscriptions</h1>
            </div>
            
            <!-- Filters -->
            <div class="flex gap-4 mb-4 items-end">
                <div class="flex-grow">
                    <label class="block font-bold mb-1 text-xs uppercase text-gray-500">Suche</label>
                    <div class="relative w-full">
                        <InputText 
                            v-model="searchQuery" 
                            placeholder="Suche nach Name oder E-Mail..."
                            @input="debouncedSearch"
                            class="w-full !h-[42px]"
                        />
                        <Button 
                            v-if="searchQuery" 
                            @click="clearSearch"
                            icon="pi pi-times"
                            class="p-button-text p-button-sm absolute right-2 top-1/2 -translate-y-1/2"
                            aria-label="Clear search"
                        />
                    </div>
                </div>

                <div v-if="isSuperAdmin" class="w-64 shrink-0">
                    <label class="block font-bold mb-1 text-xs uppercase text-gray-500">Unternehmen</label>
                    <Dropdown
                        v-model="selectedCompany"
                        :options="companyOptions"
                        optionLabel="label"
                        optionValue="value"
                        placeholder="Alle Unternehmen"
                        @change="handleFilterChange"
                        class="w-full !h-[42px] flex items-center"
                        showClear
                        filter
                    />
                </div>
            </div>
            
            <!-- Tabs -->
            <TabView :activeIndex="activeTabIndex" @tab-change="handleTabChange">
                <TabPanel header="Aktiv">
                    <DataTable 
                        :value="subscriptions.data"
                        :rowClass="getRowClass"
                        responsiveLayout="scroll"
                        :paginator="false"
                        class="p-datatable-sm"
                    >
                        <Column field="customer" header="Kundenname">
                            <template #body="slotProps">
                                {{ slotProps.data.customer?.first_name }} {{ slotProps.data.customer?.last_name }}
                            </template>
                        </Column>
                        <Column field="customer.email" header="Kunden-E-Mail" />
                        <Column field="unlimited_plan.name" header="Unlimited Plan Name" />
                        <Column field="date" header="Datum (Von)">
                            <template #body="slotProps">
                                {{ formatDate(slotProps.data.date) }}
                            </template>
                        </Column>
                        <Column field="date_to" header="Datum Bis (Bis)">
                            <template #body="slotProps">
                                {{ formatDate(slotProps.data.date_to) }}
                            </template>
                        </Column>
                    </DataTable>
                </TabPanel>
                
                <TabPanel header="Abgelaufen">
                    <DataTable 
                        :value="subscriptions.data"
                        responsiveLayout="scroll"
                        :paginator="false"
                        class="p-datatable-sm"
                    >
                        <Column field="customer" header="Kundenname">
                            <template #body="slotProps">
                                {{ slotProps.data.customer?.first_name }} {{ slotProps.data.customer?.last_name }}
                            </template>
                        </Column>
                        <Column field="customer.email" header="Kunden-E-Mail" />
                        <Column field="unlimited_plan.name" header="Unlimited Plan Name" />
                        <Column field="date" header="Datum (Von)">
                            <template #body="slotProps">
                                {{ formatDate(slotProps.data.date) }}
                            </template>
                        </Column>
                        <Column field="date_to" header="Datum Bis (Bis)">
                            <template #body="slotProps">
                                {{ formatDate(slotProps.data.date_to) }}
                            </template>
                        </Column>
                        <Column field="renewal_status" header="Erneuerungsstatus">
                            <template #body="slotProps">
                                <i 
                                    :class="slotProps.data.closed_at ? 'pi pi-times text-red-500' : 'pi pi-check text-green-500'"
                                    style="font-size: 1.2rem;"
                                />
                            </template>
                        </Column>
                    </DataTable>
                </TabPanel>
                
                <TabPanel header="Alle">
                    <DataTable 
                        :value="subscriptions.data"
                        :rowClass="getRowClass"
                        responsiveLayout="scroll"
                        :paginator="false"
                        class="p-datatable-sm"
                    >
                        <Column field="customer" header="Kundenname">
                            <template #body="slotProps">
                                {{ slotProps.data.customer?.first_name }} {{ slotProps.data.customer?.last_name }}
                            </template>
                        </Column>
                        <Column field="customer.email" header="Kunden-E-Mail" />
                        <Column field="unlimited_plan.name" header="Unlimited Plan Name" />
                        <Column field="date" header="Datum (Von)">
                            <template #body="slotProps">
                                {{ formatDate(slotProps.data.date) }}
                            </template>
                        </Column>
                        <Column field="date_to" header="Datum Bis (Bis)">
                            <template #body="slotProps">
                                {{ formatDate(slotProps.data.date_to) }}
                            </template>
                        </Column>
                        <Column field="status" header="Status" v-if="showStatusColumn">
                            <template #body="slotProps">
                                <Badge 
                                    :value="getStatusLabel(slotProps.data.status)"
                                    :severity="getStatusSeverity(slotProps.data.status)"
                                />
                            </template>
                        </Column>
                        <Column field="renewal_status" header="Erneuerungsstatus" v-if="showStatusColumn">
                            <template #body="slotProps">
                                <i 
                                    :class="slotProps.data.closed_at ? 'pi pi-times text-red-500' : 'pi pi-check text-green-500'"
                                    style="font-size: 1.2rem;"
                                />
                            </template>
                        </Column>
                    </DataTable>
                </TabPanel>
            </TabView>
            
            <!-- Pagination -->
            <div v-if="subscriptions.links && subscriptions.links.length > 3" class="mt-4">
                <nav class="flex justify-center items-center space-x-1">
                    <template v-for="link in subscriptions.links" :key="link.label">
                        <Link 
                            v-if="link.url"
                            :href="link.url"
                            class="px-3 py-2 text-sm rounded-md transition-colors duration-200"
                            :class="{
                                'bg-blue-500 text-white font-medium': link.active,
                                'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300': !link.active
                            }"
                            preserve-scroll
                            v-html="link.label"
                        />
                        <span 
                            v-else
                            class="px-3 py-2 text-sm text-gray-400 cursor-not-allowed"
                            v-html="link.label"
                        />
                    </template>
                </nav>
                
                <!-- Page info -->
                <div class="text-center mt-2 text-sm text-gray-600">
                    Zeige {{ subscriptions.from || 0 }} bis {{ subscriptions.to || 0 }} von {{ subscriptions.total }} Einträgen
                </div>
            </div>
        </div>
    </AdminLayout>
</template>

<script setup>
import { ref, computed } from 'vue';
import { Inertia } from '@inertiajs/inertia';
import { Link } from '@inertiajs/inertia-vue3';
import { debounce } from 'lodash';
import AdminLayout from '../../../Layouts/AdminLayout.vue';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import Badge from 'primevue/badge';
import Dropdown from 'primevue/dropdown';

const props = defineProps({
    subscriptions: {
        type: Object,
        required: true
    },
    filters: {
        type: Object,
        default: () => ({
            tab: 'active',
            search: '',
            company_id: null
        })
    },
    isSuperAdmin: {
        type: Boolean,
        default: false
    },
    companyOptions: {
        type: Array,
        default: () => []
    },
    showStatusColumn: {
        type: Boolean,
        default: false
    }
});

const searchQuery = ref(props.filters.search || '');
const selectedCompany = ref(props.filters.company_id);

const activeTabIndex = computed(() => {
    const tabs = ['active', 'expired', 'all'];
    return tabs.indexOf(props.filters.tab || 'active');
});

const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const day = d.getDate().toString().padStart(2, '0');
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const year = d.getFullYear();
    return `${day}.${month}.${year}`;
};

const getRowClass = (data) => {
    if (!data || !data.row_color) return '';
    
    switch (data.row_color) {
        case 'red':
            return 'bg-red-100';
        case 'orange':
            return 'bg-orange-100';
        default:
            return '';
    }
};

const getStatusLabel = (status) => {
    return status === 'active' ? 'Aktiv' : 'Abgelaufen';
};

const getStatusSeverity = (status) => {
    return status === 'active' ? 'success' : 'danger';
};

const handleTabChange = (event) => {
    const tabs = ['active', 'expired', 'all'];
    // Clear search when switching tabs
    searchQuery.value = '';
    const params = {
        tab: tabs[event.index],
        search: '',  // Clear search
        page: 1,     // Reset to page 1
    };

    if (props.isSuperAdmin) {
        params.company_id = selectedCompany.value;
    }

    Inertia.get(route('admin.subscriptions.index'), params, { 
        preserveState: true,
        preserveScroll: true
    });
};

const handleFilterChange = () => {
    const params = {
        tab: props.filters.tab,
        search: searchQuery.value,
        page: 1
    };

    if (props.isSuperAdmin) {
        params.company_id = selectedCompany.value;
    }
    
    Inertia.get(route('admin.subscriptions.index'), params, { 
        preserveState: true,
        preserveScroll: true
    });
};

const debouncedSearch = debounce(() => {
    handleFilterChange();
}, 500);

const clearSearch = () => {
    searchQuery.value = '';
    handleFilterChange();
};

const exportPdf = () => {
    const params = new URLSearchParams({
        tab: props.filters.tab || 'active',
        search: props.filters.search || ''
    });
    
    // Trigger PDF download
    window.location.href = `/admin/subscriptions/export?${params.toString()}`;
};
</script>

<style scoped>
/* Custom styles for row colors */
:deep(.bg-red-100) {
    background-color: #fca5a5 !important; /* Stronger red for urgent (0-1 days) */
}

:deep(.bg-orange-100) {
    background-color: #fef3c7 !important; /* Much lighter orange/yellow for warning (3-5 days) */
}

/* Consistent padding for header and body cells */
:deep(.p-datatable .p-datatable-thead > tr > th) {
    padding: 0.75rem !important;
}

:deep(.p-datatable .p-datatable-tbody > tr > td) {
    padding: 0.75rem !important;
    border-bottom: none !important;
}

/* Remove border from table rows */
:deep(.p-datatable .p-datatable-tbody > tr) {
    border-bottom: none !important;
}

:deep(.p-datatable .p-datatable-tbody > tr > td) {
    border: none !important;
}

/* Adjust margin on tab panels */
:deep(.p-tabview-panels) {
    margin: 1rem 0 0 0 !important;
    padding: 0 !important;
}
</style>